import { useEffect, useState } from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Autocomplete } from "@/compat/Autocomplete";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import { validEmailRegex } from "../../utils/RegexUtils";
import type { SaveShareGroupRequest, ShareGroupDTO } from "../../types/shareGroupDtos";

const ALL_SITES = "__all__";
const SELECT_ALL = "__select_all__";
const NAME_MAX = 100;

const useStyles = makeStyles((theme) => ({
    header: { padding: theme.spacing(3, 3, 0) },
    title: { padding: 0, margin: 0 },
    field: { marginTop: theme.spacing(2) },
    hint: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(0.5) },
    selectAllOption: { fontWeight: 600 },
    error: { "&.MuiChip-outlined": { border: "1px solid red", color: "red" } },
}));

const toEmail = (item: any) => (typeof item === "string" ? item.trim() : item?.email);
const contactName = (c: any) => `${c?.firstName ?? ""} ${c?.lastName ?? ""}`.trim();

const filterContacts = createFilterOptions<any>({
    stringify: (option) => `${option.firstName ?? ""} ${option.lastName ?? ""} ${option.email ?? ""}`,
});

interface ShareGroupEditorDialogProps {
    open: boolean;
    group?: ShareGroupDTO | null;
    contacts: Record<string, any>;
    sites: any[];
    accountExternalId: string;
    isSaving?: boolean;
    onClose: () => void;
    onSave: (group: SaveShareGroupRequest) => void;
}

export default function ShareGroupEditorDialog({
    open,
    group,
    contacts,
    sites,
    accountExternalId,
    isSaving,
    onClose,
    onSave,
}: ShareGroupEditorDialogProps) {
    const classes = useStyles();

    const [name, setName] = useState("");
    const [visibility, setVisibility] = useState<string>(ALL_SITES);
    const [members, setMembers] = useState<any[]>([]);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (!open) return;
        setName(group?.name ?? "");
        setVisibility(group?.siteExternalId ? group.siteExternalId : ALL_SITES);
        setIsActive(group?.isActive ?? true);
        setMembers(
            (group?.members ?? []).map((m) =>
                m.contactExternalId ? contacts?.[m.contactExternalId] ?? { externalId: m.contactExternalId, unresolved: true } : m.email
            )
        );
    }, [open, group, contacts, accountExternalId]);

    // "All sites" offers everyone on the account, otherwise just that site's users.
    const contactOptions = Object.values(contacts ?? {}).filter(
        (c: any) => visibility === ALL_SITES || c?.accountIds?.includes(visibility)
    );

    const selectedContactIds = new Set(members.filter((m) => typeof m !== "string").map((m: any) => m.externalId));
    const addableContacts = contactOptions.filter((c: any) => !selectedContactIds.has(c.externalId));

    // Sentinel option at the top of the list; picking it swaps in everyone remaining.
    const memberOptions =
        addableContacts.length > 1 ? [{ externalId: SELECT_ALL }, ...contactOptions] : contactOptions;

    const onMembersChange = (value: any[]) => {
        if (value.some((v: any) => v?.externalId === SELECT_ALL)) {
            setMembers([...members, ...addableContacts]);
            return;
        }
        setMembers([...value]);
    };

    const hasInvalidEmail = members.some((m) => typeof m === "string" && !m.trim().match(validEmailRegex));
    const canSave = !!name.trim() && members.length > 0 && !hasInvalidEmail && !isSaving;

    const save = () =>
        onSave({
            id: group?.id,
            name: name.trim(),
            accountExternalId,
            siteExternalId: visibility === ALL_SITES ? null : visibility,
            isActive,
            members: members.map((m) =>
                typeof m === "string" ? { email: m.trim() } : { contactExternalId: m.externalId }
            ),
        });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <div className={classes.header}>
                <DialogTitle className={classes.title}>{group ? "Edit share group" : "Create share group"}</DialogTitle>
                <DialogContentText className={classes.title}>
                    Save a reusable set of recipients, then share a risk assessment to the whole group in one go.
                </DialogContentText>
            </div>
            <DialogContent>
                <TextField
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    label="Group name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
                    className={classes.field}
                />

                <div className={classes.field}>
                    <ResponsiveSelect
                        size="small"
                        required
                        fullWidth
                        label="Available for"
                        options={[{ value: ALL_SITES, label: "All sites" }, ...sites.map((s: any) => ({ value: s.externalId, label: s.name }))]}
                        optionValueKey="value"
                        optionLabelKey="label"
                        value={visibility}
                        onChange={(e: any) => setVisibility(e.target.value)}
                    />
                    <Typography variant="caption" className={classes.hint}>
                        Choose “All sites” to use this group anywhere, or limit it to one site.
                    </Typography>
                </div>

                <div className={classes.field}>
                    <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        filterSelectedOptions
                        id="share-group-members"
                        options={memberOptions}
                        value={members}
                        onChange={(e: any, value: any) => onMembersChange(value)}
                        filterOptions={filterContacts}
                        getOptionLabel={(option: any) =>
                            typeof option === "string"
                                ? option
                                : option.externalId === SELECT_ALL
                                    ? "Select all"
                                    : contactName(option) || option.email || option.externalId
                        }
                        getOptionKey={(option: any) => (typeof option === "string" ? option : option.externalId)}
                        isOptionEqualToValue={(option: any, value: any) =>
                            typeof option === "string" || typeof value === "string"
                                ? toEmail(option) === toEmail(value)
                                : option.externalId === value.externalId
                        }
                        renderOption={(option: any) =>
                            option.externalId === SELECT_ALL ? (
                                <Typography variant="body2" color="primary" className={classes.selectAllOption}>
                                    Select all ({addableContacts.length})
                                </Typography>
                            ) : (
                                <div>
                                    <Typography variant="body2">{contactName(option) || option.email}</Typography>
                                    {contactName(option) && (
                                        <Typography variant="caption" color="textSecondary">
                                            {option.email}
                                        </Typography>
                                    )}
                                </div>
                            )
                        }
                        renderTags={(value: any[], getTagProps: any) =>
                            value.map((option: any, index: number) => {
                                const label =
                                    typeof option === "string"
                                        ? option
                                        : option.unresolved
                                            ? "No longer available"
                                            : contactName(option) || option.email;
                                const invalid =
                                    (typeof option === "string" && !option.trim().match(validEmailRegex)) || option?.unresolved;
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        classes={invalid ? { outlined: classes.error } : undefined}
                                        variant="outlined"
                                        label={label}
                                        title={typeof option === "string" ? option : option.email}
                                    />
                                );
                            })
                        }
                        renderInput={(params: any) => (
                            <TextField {...params} variant="outlined" label="Members" placeholder="Add a person or type an email…" />
                        )}
                    />
                    <Typography variant="caption" className={classes.hint}>
                        {visibility === ALL_SITES
                            ? "Pick people from this account, or type any email address for someone outside it."
                            : "Pick people from the selected site, or type any email address for someone outside it."}
                    </Typography>
                </div>

                <FormControlLabel
                    className={classes.field}
                    control={<Switch size="small" color="secondary" checked={isActive} onChange={() => setIsActive(!isActive)} />}
                    label="Active (available to select when sharing)"
                />
            </DialogContent>
            <DialogActions>
                <Button color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" disabled={!canSave} onClick={save}>
                    {group ? "Save changes" : "Create group"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
